<template>
  <div>

    <div class="mod-access">
      <h3 class="tab-title">{{ $t("ACCESS_MANAGER_RIGHTS_MANAGER") }}</h3>

      <div class="access-body">
        <span class="text-header">{{ $t("ACCESS_MANAGER_GRANT_ACCESS") }}</span>
        <form action class="inp-rad">
          <input
            type="radio"
            name="radio"
            id="x1"
            @click="access_type = true"
            :checked="access_type"
          />
          <label for="x1">{{ $t("ACCESS_MANAGER_FULL_ACCESS") }}</label>
          <input
            type="radio"
            name="radio"
            id="x2"
            @click="access_type = false"
            :checked="!access_type"
          />
          <label for="x2">{{ $t("ACCESS_MANAGER_READ_ONLY") }}</label>
        </form>
        <form @submit.prevent="giveAccess" class="inp-btn">
          <input
            type="text"
            class="inp-st"
            placeholder="Email"
            v-model="admin_email"
          />
          <button class="btn-st-red" type="submit">
            {{ $t("ACCESS_MANAGER_GIVE_ACCESS") }}
          </button>
        </form>
      </div>
      <div class="tab-wrapper">
        <table class="acc-tab">
          <thead>
            <tr>
              <th>{{ $t("ACCESS_MANAGER_EMAIL") }}</th>
              <th>{{ $t("ACCESS_MANAGER_ACCESS") }}</th>
              <th>{{ $t("ACCESS_MANAGER_ACTION") }}</th>
            </tr>
          </thead>
          <tbody :key="index" v-for="(admin, index) in getGrantedAccesses">
            <tr>
              <td>{{ admin.email }}</td>
              <td>
                <div class="drpdown">
                  <input type="checkbox" :id="'z1_' + index" name />
                  <label :for="'z1_' + index">{{
                    admin.access_type
                      ? $t("ACCESS_MANAGER_FULL_ACCESS")
                      : $t("ACCESS_MANAGER_READ_ONLY")
                  }}</label>
                  <span class="expand"></span>
                  <ul>
                    <li
                      v-if="!admin.access_type"
                      @click="
                        changeAccess({ email: admin.email, access_type: 1 })
                      "
                    >
                      {{ $t("ACCESS_MANAGER_FULL_ACCESS") }}
                    </li>
                    <li
                      v-if="admin.access_type"
                      @click="
                        changeAccess({ email: admin.email, access_type: 0 })
                      "
                    >
                      {{ $t("ACCESS_MANAGER_READ_ONLY") }}
                    </li>
                  </ul>
                </div>
              </td>
              <td>
                <span class="remove" @click="deleteAdmin(admin.email)">{{
                  $t("ACCESS_MANAGER_REMOVE_ACCESS")
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
        <!-- Container for inline alerts -->
        <div ref="alertContainer"></div>
      </div>

      <!-- Category Access Management Section -->
      <div class="category-access-section" v-if="categoriesWithAccess.length">
        <h3 class="tab-title">{{ $t("CATEGORY_ACCESS_MANAGEMENT") }}</h3>
        
        <div class="tab-wrapper">
          <table class="acc-tab">
            <thead>
              <tr>
                <th>{{ $t("CATEGORY_ACCESS_CATEGORY") }}</th>
                <th>{{ $t("ACCESS_MANAGER_EMAIL") }}</th>
                <th>{{ $t("ACCESS_MANAGER_ACTION") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(accessItem, index) in flattenedCategoryAccess" :key="index">
                <td>
                  <div class="category-info">
                    <span :class="`cat-indicator ${getCategoryColorClass(accessItem.category.id)}`"></span>
                    <span class="category-name">{{ accessItem.category.name }}</span>
                  </div>
                </td>
                <td>{{ accessItem.email }}</td>
                <td>
                  <span class="remove" @click="removeCategoryAccessFromUser(accessItem.category.id, accessItem.email)">
                    {{ $t("ACCESS_MANAGER_REMOVE_ACCESS") }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- No Category Access Message -->
      <div v-else-if="categoriesLoaded && !categoriesWithAccess.length" class="no-category-access">
        <p>{{ $t("CATEGORY_ACCESS_NO_SHARED") }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import { INTRO_SET_ACCESS_MANAGER_VISIBLE } from "../../store/actions/intro";
import {
  ADMIN_DELETE,
  ADMIN_SET,
  ADMIN_CHANGE_ACCESS,
} from "../../store/actions/admin";
import {
  CATEGORIES_SET_LIST,
  CATEGORIES_SET_ACCESS_LIST,
  CATEGORIES_REMOVE_ACCESS,
} from "../../store/actions/categories";
import i18n from "@/i18nSetup";

import module from '@/models/AxiosModule'

export default {
  name: "AccessManager",
  computed: {
    ...mapGetters(["getGrantedAccesses", "getCategories", "getCategoriesAccessList"]),
    
    categoriesWithAccess() {
      return this.categoriesWithAccessData.filter(category => 
        category.access_list && category.access_list.length > 0
      );
    },
    
    flattenedCategoryAccess() {
      const flattened = [];
      this.categoriesWithAccess.forEach(category => {
        category.access_list.forEach(access => {
          flattened.push({
            category: category,
            email: access.granted_to_email
          });
        });
      });
      return flattened;
    }
  },

  data() {
    return {
      access_type: false,
      admin_email: undefined,
      emptyAlertInstance: null,
      categoriesWithAccessData: [],
      categoriesLoaded: false,
    };
  },
  mounted() {
    this.fetchGrantedAccesses();
    this.fetchCategoriesWithAccess();
  },
  watch: {
    getGrantedAccesses(newVal) {
      if (newVal.length === 0) {
        this.showEmptyTableAlert();
      } else {
        this.clearAlert();
      }
    },
  },
  methods: {
    ...mapActions({
      deleteAdmin: ADMIN_DELETE,
      changeAccess: ADMIN_CHANGE_ACCESS,
      fetchGrantedAccesses: ADMIN_SET,
      loadCategories: CATEGORIES_SET_LIST,
      setCategoryAccessList: CATEGORIES_SET_ACCESS_LIST,
      removeCategoryAccess: CATEGORIES_REMOVE_ACCESS,
    }),
    ...mapMutations([INTRO_SET_ACCESS_MANAGER_VISIBLE]),

    giveAccess() {
      const axios = module.Axios;
      axios
        .post("create-admin-access", {
          access_type: this.access_type,
          admin_email: this.admin_email,
        })
        .then(() => {
          this.$store.dispatch(ADMIN_SET).then(() => {
            if (this.getGrantedAccesses.length > 0) {
              this.clearAlert();
            }
          });
        })
        .catch((error) => {
          if (
            error?.response?.data
          ) {
            //console.error("Error Data:", error.response.data);
            if (error.response.data?.error_code) {
              this.showInlineAlert(this.$t(error.response.data?.error_code));
            } else {
              this.showInlineAlert(this.$t("OTHER_ERROR"));
            }
          } else {
            //console.error("Error:", error.message); // Log error message
          }
        });
    },
    showEmptyTableAlert() {
      this.emptyAlertInstance = this.$alert.show({
        message: i18n.t("ACCESS_MANAGER_NO_ACCESS"),
        type: "inline",
        color: "white",
        component: this,
        duration: 99999,
      });
    },
    clearAlert() {
      if (this.emptyAlertInstance) {
        this.emptyAlertInstance.close();
        this.emptyAlertInstance = null;
      }
    },
    showInlineAlert(message) {
      this.$alert.show({
        message: message,
        type: 'inline',
        color: '',
        component: this,
        target: this.$refs.alertContainer,
      });
    },

    async fetchCategoriesWithAccess() {
      try {
        await this.loadCategories();
        const categories = this.getCategories || [];
        const axios = module.Axios;
        const categoriesWithAccess = await Promise.all(
          categories.map(async (category) => {
            try {
              const response = await axios.post('get-category-access', {
                id: category.id
              });
              
              const accessList = response.data.access_list || [];
              
              return {
                ...category,
                access_list: accessList
              };
            } catch (error) {
              return {
                ...category,
                access_list: []
              };
            }
          })
        );
        
        this.categoriesWithAccessData = categoriesWithAccess;
        this.categoriesLoaded = true;
      } catch (error) {
        this.categoriesWithAccessData = [];
        this.categoriesLoaded = true;
      }
    },

    async removeCategoryAccessFromUser(categoryId, email) {
      try {
        await this.removeCategoryAccess({
          categoryId: categoryId,
          email: email
        });
        await this.fetchCategoriesWithAccess();
        
        this.showInlineAlert(this.$t('CATEGORY_ACCESS_REMOVED_SUCCESS'));
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CATEGORY_ACCESS_REMOVE_ERROR'));
        }
      }
    },

    getCategoryColorClass(categoryId) {
      if (!categoryId) return 'gray-back';
      const stored = localStorage.getItem('categoryColors');
      const colors = stored ? JSON.parse(stored) : {};
      const color = colors[categoryId] || 'gray';
      
      return color + '-back';
    },
  },
};
</script>
